/**
 * WebSocket client with binary protocol handling, reconnection logic,
 * and message queue for offline buffering.
 */

import { ClientMessage, ServerMessage } from './client_message_protocol';
import { GameClientWorldState } from './game_client_world_state';

const WS_URL = `wss://${window.location.hostname}`;
const RECONNECT_DELAY_MS = 2000;
const MAX_QUEUE_SIZE = 256;

export class GameWebSocketClient {
  private ws: WebSocket | null = null;
  private messageQueue: ClientMessage[] = [];
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private worldState: GameClientWorldState;
  private buffer: ArrayBuffer = new ArrayBuffer(0);

  constructor(worldState: GameClientWorldState) {
    this.worldState = worldState;
  }

  connect(): void {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    console.info(`Connecting to ${WS_URL}`);
    this.ws = new WebSocket(WS_URL);
    this.ws.binaryType = 'arraybuffer';

    this.ws.onopen = () => {
      console.info('WebSocket connected');
      this.flushQueue();
    };

    this.ws.onmessage = (event: MessageEvent) => {
      this.handleMessage(event.data as ArrayBuffer);
    };

    this.ws.onclose = (event) => {
      console.warn(`WebSocket closed (code=${event.code}), reconnecting in ${RECONNECT_DELAY_MS}ms`);
      this.scheduleReconnect();
    };

    this.ws.onerror = () => {
      console.error('WebSocket error');
    };
  }

  send(msg: ClientMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const payload = new TextEncoder().encode(JSON.stringify(msg));
      const frame = new ArrayBuffer(2 + payload.byteLength);
      const view = new DataView(frame);
      view.setUint16(0, payload.byteLength, false);
      new Uint8Array(frame, 2).set(payload);
      this.ws.send(frame);
    } else {
      if (this.messageQueue.length < MAX_QUEUE_SIZE) {
        this.messageQueue.push(msg);
      }
    }
  }

  private flushQueue(): void {
    while (this.messageQueue.length > 0) {
      const msg = this.messageQueue.shift()!;
      this.send(msg);
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, RECONNECT_DELAY_MS);
  }

  private handleMessage(data: ArrayBuffer): void {
    // Node bridge assembles complete frames and sends as individual WebSocket messages
    try {
      const text = new TextDecoder().decode(data);
      const msg: ServerMessage = JSON.parse(text);
      if (msg.type === 'WorldState') {
        this.worldState.applyWorldSnapshot(msg);
      } else if (msg.type === 'DeltaState') {
        this.worldState.applyDelta(msg.deltas);
      } else {
        this.worldState.applyMessage(msg);
      }
    } catch (err) {
      console.error('Failed to parse server message:', err);
    }
  }
}
